import React from "react";
import {
  Box,
  Typography,
  Paper,
  useTheme,
  Button,
  Grid,
  Tooltip,
  useMediaQuery,
  Alert,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";

import {
  MARKETING_EVENTS,
  getDemoUrl,
  getDemoUrlWithSource,
  trackMarketingEvent,
} from "../utils/demoTracking";

const Demo = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Resolved once per mount: the URL carries a session marker and should stay stable while the
  // visitor is on the page, so the iframe is not torn down and rebuilt on every render.
  const demoUrl = React.useMemo(() => getDemoUrlWithSource(), []);

  const openInNewTab = () => {
    trackMarketingEvent(MARKETING_EVENTS.DEMO_CTA_CLICKED);
    // noopener: the demo would otherwise get a window.opener handle back to this page.
    window.open(demoUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Box p={4} sx={{ backgroundColor: theme.palette.background.default }}>
      <Box display="flex" justifyContent="center" alignItems="center" mb={3}>
        <Tooltip title="Programming" arrow placement="top">
          <span>
            <img src="badges/Programming.svg" alt="Programming" height={70} />
          </span>
        </Tooltip>
      </Box>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        align="center"
        sx={{ color: theme.palette.text.primary, fontWeight: "bold", pb: 1 }}
      >
        Explore Our Interactive Demo
      </Typography>
      <Typography
        component="p"
        align="center"
        sx={{
          color: theme.palette.text.secondary,
          maxWidth: 760,
          mx: "auto",
          pb: 3,
        }}
      >
        A complete, working copy of BadgeReg loaded with sample data. Sign in as
        a parent, counselor, admin or owner and try anything you like: the
        sandbox resets itself every hour, and nothing you do affects a real
        event.
      </Typography>
      <Paper
        elevation={3}
        sx={{
          padding: 3,
          marginBottom: 3,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Grid container spacing={3} justifyContent="center" alignItems="center">
          <Grid item xs={12} md={10}>
            {isMobile ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                The demo works best in its own tab on a phone: there is a lot of
                screen to use.
              </Alert>
            ) : null}

            {/*
              The demo is embedded on phones too, not just desktops. It is a subdomain of this site,
              so the browser treats the frame as same-site and the demo's own login cookie survives,
              which is what makes an embedded, interactive demo possible at all here. A third-party
              domain would be partitioned by Safari and Firefox and could not hold a session.
            */}
            <Box
              sx={{
                border: "1px solid",
                borderColor: theme.palette.divider,
                borderRadius: 1,
                overflow: "hidden",
                // 750px flat overflowed short laptop screens (a 768px-tall display had no room for
                // the frame plus the buttons under it), so it is capped against the viewport.
                height: isMobile ? "70vh" : "min(750px, 78vh)",
                mb: 2,
              }}
            >
              <iframe
                src={demoUrl}
                title="BadgeReg interactive demo"
                width="100%"
                height="100%"
                loading="lazy"
                style={{ border: "none", display: "block" }}
                // Enough to run the app and complete a PayPal sandbox checkout, and no more.
                // allow-same-origin is required for the demo to reach its own storage and session;
                // it is safe here because the frame is our own subdomain.
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals"
              />
            </Box>

            <Box display="flex" justifyContent="center">
              <Button
                variant="contained"
                color="primary"
                startIcon={<LaunchIcon />}
                fullWidth={isMobile}
                sx={{ mb: 1 }}
                onClick={openInNewTab}
              >
                Open Demo in New Tab
              </Button>
            </Box>
            <Typography
              variant="caption"
              component="p"
              align="center"
              sx={{ color: theme.palette.text.secondary }}
            >
              Trouble loading the demo?{" "}
              <a href={getDemoUrl()} target="_blank" rel="noopener noreferrer">
                Open it directly
              </a>
              .
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Demo;
